import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, OverlayView } from "@react-google-maps/api";
import { Button } from "./ui/button";
import { Pencil, Trash2, Loader2, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";
import BrandPin from "./BrandPin";

const LIBS = ["places", "drawing"];
const mapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const defaultCenter = { lat: 19.4326, lng: -99.1332 };

// Same Uber-light tile palette used in MapPicker for visual consistency.
const UBER_LIGHT_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6b6b" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5f3df" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ffd166" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#e6bf5c" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c8dff6" }] },
];

/**
 * Editor de polígonos de cobertura.
 *
 *  - `polygons` (controlado): lista de polígonos. Cada polígono es una
 *    lista de [lat, lng].
 *  - `onChange(newPolygons)`: callback con la lista actualizada.
 *  - `pin`: { lat, lng } — pin de ubicación de la empresa (solo lectura).
 *  - `color`: color del trazo / relleno (usa el primary de la empresa).
 *
 * Implementación: gestiona los `google.maps.Polygon` imperativamente en
 * un `useEffect` para evitar bugs del componente JSX <Polygon> al
 * cambiar listas dinámicamente (mismo patrón que TrackingMap).
 */
export default function ServiceAreaEditor({
  polygons = [],
  onChange,
  pin,
  color = "#0A0A0A",
  height = 360,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapKey || "",
    libraries: LIBS,
  });

  const mapRef = useRef(null);
  const drawingMgrRef = useRef(null);
  const polyRefs = useRef([]); // google.maps.Polygon[]
  const [drawing, setDrawing] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // Auto-fit map around polygons. Cuando no hay polígonos, centramos
  // en el pin de la empresa con un zoom amigable.
  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const hasPolys = polygons.some((p) => p && p.length >= 2);
    const apply = () => {
      if (hasPolys) {
        const bounds = new window.google.maps.LatLngBounds();
        polygons.forEach((poly) => {
          poly.forEach(([lat, lng]) => {
            bounds.extend({ lat: Number(lat), lng: Number(lng) });
          });
        });
        map.fitBounds(bounds, 80);
        return;
      }
      if (pin?.lat && pin?.lng) {
        map.panTo({ lat: pin.lat, lng: pin.lng });
        map.setZoom(14);
      }
    };
    const div = map.getDiv();
    if (div?.offsetWidth && div?.offsetHeight) {
      apply();
    } else {
      window.google.maps.event.addListenerOnce(map, "idle", apply);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapReady, pin?.lat, pin?.lng, polygons.length]);

  // Re-render polygons imperatively whenever the controlled list changes.
  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current) return;
    // Wipe previous renders.
    polyRefs.current.forEach((p) => p.setMap(null));
    polyRefs.current = [];

    polygons.forEach((path, idx) => {
      const poly = new window.google.maps.Polygon({
        paths: path.map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) })),
        strokeColor: color,
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: color,
        fillOpacity: 0.25,
        editable: selectedIdx === idx,
        draggable: false,
        clickable: true,
        zIndex: selectedIdx === idx ? 5 : 1,
      });
      poly.setMap(mapRef.current);

      // Click to select.
      poly.addListener("click", () => setSelectedIdx(idx));

      // Persist edits (vertex drag, vertex insert, vertex remove).
      const persist = () => {
        const newPath = poly.getPath().getArray().map((latLng) => [latLng.lat(), latLng.lng()]);
        const next = [...polygons];
        next[idx] = newPath;
        onChange?.(next);
      };
      poly.getPath().addListener("set_at", persist);
      poly.getPath().addListener("insert_at", persist);
      poly.getPath().addListener("remove_at", persist);

      polyRefs.current.push(poly);
    });

    return () => {
      polyRefs.current.forEach((p) => p.setMap(null));
      polyRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapReady, polygons, color, selectedIdx]);

  // Configure / show the DrawingManager when entering "drawing" mode.
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    // Always tear down previous manager — its options can only be set at construction.
    if (drawingMgrRef.current) {
      drawingMgrRef.current.setMap(null);
      drawingMgrRef.current = null;
    }
    if (!drawing) return;

    const dm = new window.google.maps.drawing.DrawingManager({
      drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
      drawingControl: false,
      polygonOptions: {
        strokeColor: color,
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: color,
        fillOpacity: 0.25,
        editable: false,
        clickable: true,
      },
    });
    dm.setMap(mapRef.current);

    const listener = window.google.maps.event.addListener(dm, "polygoncomplete", (poly) => {
      const path = poly.getPath().getArray().map((latLng) => [latLng.lat(), latLng.lng()]);
      // Remove the helper polygon — we re-render via the controlled list.
      poly.setMap(null);
      if (path.length < 3) {
        toast.error("Un polígono necesita al menos 3 puntos");
      } else {
        const next = [...polygons, path];
        onChange?.(next);
        setSelectedIdx(next.length - 1);
        toast.success("Polígono añadido. Toca los puntos para ajustarlo.");
      }
      setDrawing(false);
    });

    drawingMgrRef.current = dm;
    return () => {
      window.google.maps.event.removeListener(listener);
      dm.setMap(null);
      drawingMgrRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing, isLoaded, color]);

  const removePolygon = (idx) => {
    const next = polygons.filter((_, i) => i !== idx);
    onChange?.(next);
    setSelectedIdx(null);
  };

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Error cargando Google Maps. Revisa REACT_APP_GOOGLE_MAPS_API_KEY.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-500 p-4">
        <Loader2 size={14} className="animate-spin" /> Cargando mapa...
      </div>
    );
  }

  const initialCenter = pin?.lat && pin?.lng
    ? { lat: pin.lat, lng: pin.lng }
    : polygons[0]?.[0]
      ? { lat: polygons[0][0][0], lng: polygons[0][0][1] }
      : defaultCenter;

  return (
    <div className="space-y-3" data-testid="service-area-editor">
      <div className="flex flex-wrap items-center gap-2">
        {!drawing ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setDrawing(true)}
            className="h-9 gap-2 rounded-full"
            data-testid="service-area-draw-btn"
          >
            <Plus size={14} /> Dibujar área
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDrawing(false)}
            className="h-9 gap-2 rounded-full"
            data-testid="service-area-cancel-draw-btn"
          >
            <X size={14} /> Cancelar dibujo
          </Button>
        )}
        <span className="text-xs text-neutral-500">
          {drawing
            ? "Haz clic en el mapa para añadir vértices, doble clic para cerrar el polígono."
            : `${polygons.length} ${polygons.length === 1 ? "polígono" : "polígonos"} definido${polygons.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div
        className="rounded-2xl overflow-hidden border border-neutral-200 relative"
        style={{ height }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={initialCenter}
          zoom={13}
          onLoad={onMapLoad}
          options={{
            styles: UBER_LIGHT_STYLE,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy",
            clickableIcons: false,
          }}
        >
          {pin?.lat && pin?.lng && (
            <OverlayView
              position={{ lat: pin.lat, lng: pin.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -h })}
            >
              <div data-testid="service-area-supply-pin">
                <BrandPin color={color} />
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      </div>

      {polygons.length > 0 && (
        <div className="space-y-2" data-testid="service-area-list">
          {polygons.map((p, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between rounded-xl border px-3 py-2 transition ${
                selectedIdx === idx
                  ? "border-neutral-900 bg-neutral-50"
                  : "border-neutral-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="h-6 w-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ backgroundColor: color }}
                >
                  {idx + 1}
                </div>
                <div className="text-sm">
                  <div className="font-medium">Área {idx + 1}</div>
                  <div className="text-xs text-neutral-500">{p.length} vértices</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 gap-1"
                  onClick={() => setSelectedIdx(selectedIdx === idx ? null : idx)}
                  data-testid={`service-area-edit-btn-${idx}`}
                >
                  {selectedIdx === idx ? <Check size={14} /> : <Pencil size={14} />}
                  {selectedIdx === idx ? "Listo" : "Editar"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => removePolygon(idx)}
                  data-testid={`service-area-delete-btn-${idx}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {polygons.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Debes dibujar al menos un área de cobertura para poder guardar.
        </p>
      )}
    </div>
  );
}
